import { EnumTransferDirection, FilterOperatorTypes } from '../../graphql/types';
import { ROUTES } from '../../routes';
import {
  buildTransferGroupContractAddressFilterUrlParam,
  buildTransferGroupCurrencyFilterUrlParam,
  buildTransferGroupDateAndCurrencyFilterUrlParam,
  buildTransferGroupFilterUrl,
  buildTransferGroupHashFilterObject,
  buildTransferGroupHashFilterUrlParam,
  buildTransferGroupImportsFilterUrlParam,
  buildTransferGroupMethodIdFilterUrlParam,
  buildTransferGroupTransferAddressFilterUrlParam,
} from './transferGroupsUrl';
import type { IntentAdapter } from './intents';
import { ok, unsupported, withSort } from './serialize';

function directionOf(direction: 'in' | 'out') {
  if (direction === 'in') return EnumTransferDirection.in;
  if (direction === 'out') return EnumTransferDirection.out;
  return undefined;
}

export const transferGroupsAdapter: IntentAdapter = {
  all: ({ sort }) => ok(withSort(ROUTES.allTransfers, 'transferGroups', sort)),
  byHash: ({ hashes }) => ok(buildTransferGroupHashFilterUrlParam(hashes)),
  byImports: ({ credentialIds, sort }) =>
    ok(withSort(buildTransferGroupImportsFilterUrlParam(credentialIds), 'transferGroups', sort)),
  byAccounts: ({ accountIds }) =>
    ok(
      buildTransferGroupFilterUrl({
        accounts: { operator: FilterOperatorTypes.Is, values: accountIds },
      }),
    ),
  byCurrency: ({ currency, sort }) =>
    ok(withSort(buildTransferGroupCurrencyFilterUrlParam(currency), 'transferGroups', sort)),
  byCurrencyOnDate: ({ date, currency }) =>
    ok(buildTransferGroupDateAndCurrencyFilterUrlParam(date, currency)),
  byDateCurrencyHash: ({ date, currency, hash }) => {
    const filterQuery: Record<string, unknown> = {
      date: { fromDate: date, toDate: date },
    };
    if (currency) {
      filterQuery.currency = [{ currency, operator: FilterOperatorTypes.Is }];
    }
    if (hash) {
      filterQuery.hash = [buildTransferGroupHashFilterObject(hash)];
    }
    return ok(buildTransferGroupFilterUrl(filterQuery));
  },
  byMethodId: ({ methodId }) => ok(buildTransferGroupMethodIdFilterUrlParam(methodId)),
  byContractAddress: ({ address }) => ok(buildTransferGroupContractAddressFilterUrlParam(address)),
  byTransferAddress: ({ address, direction }) =>
    ok(buildTransferGroupTransferAddressFilterUrlParam(address, directionOf(direction))),
  byTransferGroup: ({ id }) => ok(`${ROUTES.allTransfers}?transferGroupId=${encodeURIComponent(id)}`),
  workpaperTransactions: ({
    credentialIds,
    reconIdentifier,
    contractAddress,
    methodId,
    manualImportsOnly,
    fromDate,
    toDate,
  }) => {
    if (reconIdentifier) {
      return unsupported('Recon identifier filters are not available on All Transfers');
    }
    if (manualImportsOnly) {
      return unsupported('Manual import filters are not available on All Transfers');
    }

    const filterQuery: Record<string, unknown> = {};
    if (credentialIds?.length) {
      filterQuery.imports = { operator: FilterOperatorTypes.Is, values: credentialIds };
    }
    if (contractAddress) {
      filterQuery.contractAddress = [
        { key: 'contractAddress', operator: FilterOperatorTypes.Is, value: contractAddress },
      ];
      if (methodId) {
        filterQuery.methodId = [{ key: 'methodId', operator: FilterOperatorTypes.Is, value: methodId }];
      }
    }
    if (fromDate || toDate) {
      filterQuery.date = { ...(fromDate ? { fromDate } : {}), ...(toDate ? { toDate } : {}) };
    }
    return ok(withSort(buildTransferGroupFilterUrl(filterQuery), 'transferGroups', 'oldestFirst'));
  },
  pricingAnomalies: () => unsupported('pricing anomalies are not modeled for transfer groups'),
  missingPrices: ({ fromDate }) =>
    ok(
      withSort(
        buildTransferGroupFilterUrl({
          missingPrice: true,
          ...(fromDate ? { date: { fromDate } } : {}),
        }),
        'transferGroups',
        'oldestFirst',
      ),
    ),
  byReconIdentifier: () => unsupported('recon identifier is not modeled for transfer groups'),
  byTxnTopicFingerprint: () => unsupported('txn topic fingerprints are not modeled for transfer groups'),
  byTransferTopicFingerprint: () =>
    unsupported('transfer topic fingerprints are not modeled for transfer groups'),
};
